import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import styled from 'styled-components';
import { Dropdown, Menu } from 'antd';
import ArrowDownIcon from '@/assets/mo/ArrowDown.svg?react';
import Faq from './Faq';
import type { MenuInfo } from 'rc-menu/lib/interface';

const introTabs = [
  { key: 'news', label: '최신소식' },
  { key: 'faq', label: '자주묻는질문' },
  { key: 'about', label: '올받음소개' },
  { key: 'adjusters', label: '손사소개' },
];

const defaultDescriptions: Record<string, string> = {
  news: '언론보도와 공지사항을 확인해보세요!',
  faq: '무엇이든 물어보세요. 빠르게 답변드릴게요!',
  about: '올받음을 소개합니다',
  adjusters: '올받음 손해사정사를 소개합니다',
};

const items = [
  { key: '전체', label: '전체' },
  { key: '내용', label: '내용' },
];

const FaqLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentKey = location.pathname.split('/')[2] || 'faq'; // ✅ 쿼리스트링 제거된 정확한 path
  console.log('[🔍 currentKey]', currentKey);
  const currentLabel = introTabs.find((tab) => tab.key === currentKey)?.label || '';
  const currentDesc = defaultDescriptions[currentKey];

  const [selectedTab, setSelectedTab] = useState('전체');
  const [searchKeyword, setSearchKeyword] = useState('');

  const handleTabChange = (e: MenuInfo) => {
    navigate(`/intro/${e.key}`);
  };

  const handleMenuClick = (e: MenuInfo) => {
    setSelectedTab(e.key);
  };

  const handleSearch = () => {
    navigate(
      `/intro/faq?filter=${selectedTab}&keyword=${encodeURIComponent(searchKeyword)}&page=1`,
    );
  };

  return (
    <Wrapper>
      <TabHeader>
        <Dropdown
          overlay={<Menu items={introTabs} onClick={handleTabChange} />}
          trigger={['click']}
        >
          <DropdownButton>
            <TitleText>{currentLabel}</TitleText>
            <ArrowDownIcon />
          </DropdownButton>
        </Dropdown>
        <Description>{currentDesc}</Description>
      </TabHeader>

      {currentKey === 'faq' && (
        <SearchSection>
          <SearchRow>
            <Dropdown
              overlay={<Menu items={items} onClick={handleMenuClick} />}
              trigger={['click']}
            >
              <DropdownWrapper>
                <DropdownText>{selectedTab}</DropdownText>
                <ArrowDownIcon />
              </DropdownWrapper>
            </Dropdown>

            <SearchBar hasValue={!!searchKeyword.trim()}>
              <SearchInputWrapper>
                <SearchInput
                  placeholder="검색"
                  value={searchKeyword}
                  hasValue={!!searchKeyword.trim()}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                />
              </SearchInputWrapper>
            </SearchBar>

            <SearchButton onClick={handleSearch} disabled={!searchKeyword.trim()}>
              <SearchButtonInner disabled={!searchKeyword.trim()}>검색</SearchButtonInner>
            </SearchButton>
          </SearchRow>
        </SearchSection>
      )}

      <IntroContainer>
        {currentKey !== 'faq' ? (
          <NoResultText>{`${currentKey} 페이지 준비 중입니다.`}</NoResultText>
        ) : (
          <Faq />
        )}
      </IntroContainer>
    </Wrapper>
  );
};

export default FaqLayout;

// ✅ styled-components

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  padding-top: 106px;
`;

const TabHeader = styled.div`
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DropdownButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 32px;
  font-weight: 700;
`;

const TitleText = styled.span``;

const Description = styled.div`
  font-size: 18px;
  font-weight: 400;
  text-align: start;
`;

const SearchSection = styled.div`
  display: flex;
  width: 100%;
  padding: 0 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const DropdownWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 85px;
  height: 48px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--table-line-table_line01, #bdbdbd);
  background: var(--Primary-white, #fff);
`;

const DropdownText = styled.div`
  color: var(--gray-ui_gray04, #6f6f6f);
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.32px;
`;

const SearchBar = styled.div<{ hasValue: boolean }>`
  width: 100%;
  max-width: 153px;
  display: flex;
  height: 48px;
  padding: 4px 10px;
  align-items: center;
  flex: 1 0 0;
  border-radius: 8px;
  border: 1px solid ${({ hasValue }) => (hasValue ? '#0B46DD' : '#BDBDBD')};
  background: var(--Primary-white, #fff);
`;

const SearchInputWrapper = styled.div`
  display: flex;
  padding: 0px 2px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
`;

const SearchInput = styled.input<{ hasValue: boolean }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  width: 100%;
  flex: 1 0 0;
  overflow: hidden;
  color: ${({ hasValue }) => (hasValue ? '#000C24' : '#0B46DD')};
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.32px;
  border: none;
  outline: none;
`;

const SearchButton = styled.button<{ disabled: boolean }>`
  display: flex;
  height: 48px;
  padding: 16px 12px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: ${({ disabled }) => (disabled ? '#F3F3F3' : '#0B46DD')};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  border: none;
`;

const SearchButtonInner = styled.div<{ disabled: boolean }>`
  display: flex;
  padding: 0px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: ${({ disabled }) => (disabled ? '#6F6F6F' : '#FFF')};
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.32px;
`;

const IntroContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
  padding: 16px;
`;

const NoResultText = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 0;
  color: var(--gray-ui_gray04, #6f6f6f);
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.32px;
`;
